import * as enabledApi from '@shared/api/enabledPlayApi';
import {
    BetaAllFeatureFlags,
    BetaLicenseFeatureFlags,
    LicenseEnabledFeatureFlags,
    PersonalLicenseEnabledFeatureFlags,
} from '@shared/constants/FeatureFlagValues';
import { createUUID } from '@shared/constants/Keys';
import { DefaultDisplayLocale, DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { StorageIdUser, StorageIdUserAccess } from '@shared/constants/StorageValues';
import { getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import i18n from '@shared/locales/i18n';
import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import IUserAccess from '@shared/models/components/IUserAccess';
import { AllLicenseTypes } from '@shared/models/organizations/AllLicenseTypes';
import OrganizationModelLicense, {
    OrganizationModelLicenseKeys,
} from '@shared/models/organizations/OrganizationModelLicenses';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import TosConfigModel from '@shared/models/tos/TosConfigModel';
import CapabilityFacetGroupModel from '@shared/models/users/CapabilityFacetGroupModel';
import UpdateEmailPreferencesRequest from '@shared/models/users/UpdateEmailPreferencesRequest';
import UpdateUserCapabilitiesRequest from '@shared/models/users/UpdateUserCapabilitiesRequest';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import UserLicenseAccessModel from '@shared/models/users/UserLicenseAccessModel';
import UserModel from '@shared/models/users/UserModel';
import UserPreferencesModel from '@shared/models/users/UserPreferencesModel';
import UserWithCapabilitiesModel from '@shared/models/users/UserWithCapabilitiesModel';
import type { ReactElement, ReactNode } from 'react';
import { create } from 'zustand';
import { publish, subscribe } from '../helpers/EventCallback';

export const currentUserInitiatorId = createUUID();
const fallbackVoiceUrl = require('../../public/models/en/model.tar.gz');

// ---- Derived-state helpers (pure functions, no store dependency) ----
function computeUserLicenseType(access: UserLicenseAccessModel | null): AllLicenseTypes {
    if (access?.isEmployeeAccess) return 'isEmployeeAccess';
    if (access?.isStudentAccess) return 'isStudentAccess';
    if (access?.isDeveloperAccess) return 'isDeveloperAccess';
    if (access?.isPlusAccess) return 'isPlusAccess';
    return 'personal';
}

function computeUserBetaFeatureFlags(access: UserLicenseAccessModel | null): string[] {
    const result: string[] = [...BetaLicenseFeatureFlags['personal']];
    if (access?.isEmployeeAccess) result.push(...BetaLicenseFeatureFlags['isEmployeeAccess']);
    if (access?.isStudentAccess) result.push(...BetaLicenseFeatureFlags['isStudentAccess']);
    if (access?.isDeveloperAccess) result.push(...BetaLicenseFeatureFlags['isDeveloperAccess']);
    if (access?.isPlusAccess) result.push(...BetaLicenseFeatureFlags['isPlusAccess']);
    return [...new Set(result)];
}

function computeHasNoOrgMemberships(access: UserLicenseAccessModel | null): boolean {
    return (access?.organizationLicenses?.length ?? 0) === 0;
}

function computeIsLicenseExpired(access: UserLicenseAccessModel | null): boolean {
    return access?.licenseExpirationDate ? new Date(access.licenseExpirationDate) < new Date() : false;
}

interface CurrentUserState {
    // State
    capabilityFacetGroups: CapabilityFacetGroupModel[];
    currentUser: UserWithCapabilitiesModel | null;
    errors: string[];
    // Derived state (updated when userLicenseAccess / currentUser changes)
    hasNoOrgMemberships: boolean;
    isApprovedDomain: boolean;
    isEmailVerified: boolean;
    isLicenseExpired: boolean;
    isLoading: boolean;
    isLoadingFacets: boolean;
    pendingTosConfig: TosConfigModel | null;
    showUserFallbackDialog: boolean;
    signalRConnected: boolean;
    upgradeBannerMap: Map<string, IBannerModel>;
    upgradeBannersArray: IBannerModel[];
    userBetaFeatureFlags: string[];
    userLicenseAccess: UserLicenseAccessModel | null;
    userLicenseType: AllLicenseTypes;
    userLocalePreferences: UpdateUserLocaleRequest;
    voiceModelUrl: string;

    // Actions
    addUpgradeBanner: (banner: IBannerModel, duration?: number) => void;
    checkAndUpdatePendingTos: () => Promise<void>;
    clearCurrentUser: () => void;
    deleteCurrentUser: () => Promise<IResult<UserModel>>;
    getUserFeatureFlags: () => string[];
    getUserPreferences: () => Promise<IResult<UserPreferencesModel> | undefined>;
    handleUpdateUser: (user: UserWithCapabilitiesModel) => void;
    initializeFromStorage: (isMainWindow?: boolean) => Promise<void>;
    isEduUser: () => boolean | undefined;
    isFeatureFlagEnabled: (flagName: string) => boolean;
    isFreeUser: () => boolean;
    isPlusUser: () => boolean | undefined;
    isProUser: () => boolean | undefined;
    isSdkUser: () => boolean | undefined;
    isTrialUser: () => boolean;
    isUserLicensedForAllFeatureFlags: (flagNames: string[]) => boolean;
    loadCapabilityFacetGroups: () => Promise<IResult<CapabilityFacetGroupModel[]>>;
    loadCurrentUser: () => Promise<IResult<UserWithCapabilitiesModel>>;
    loadUserLicenseAccess: () => Promise<void>;
    loadUserLicenseAccessFromStorage: () => IUserAccess | undefined;
    removeUpgradeBanner: (id: string) => void;
    requestEmailVerificationResend: () => Promise<IResult<UserModel>>;
    saveUserLocales: (req: UpdateUserLocaleRequest) => Promise<IResult<UserModel>>;
    setPendingTosConfig: (config: TosConfigModel | null) => void;
    setShowUserFallbackDialog: (v: boolean) => void;
    setSignalRConnected: (v: boolean) => void;
    updateEmailPreferences: (req: UpdateEmailPreferencesRequest) => Promise<IResult<UserModel>>;
    updatePassword: (old: string, newPwd: string, confirm: string) => Promise<IResult<UserModel>>;
    updateUserCapabilities: (req: UpdateUserCapabilitiesRequest) => Promise<IResult<UserWithCapabilitiesModel>>;
    updateUserLicenseAccess: (access: UserLicenseAccessModel, updateLocal?: boolean) => Promise<void>;
    updateUserLocales: (req: UpdateUserLocaleRequest, isMainWindow?: boolean) => Promise<void>;
    updateUserPermissions: (value: boolean) => void;
    updateUserPreferences: (req: UserPreferencesModel) => Promise<IResult<UserModel>>;
}

const defaultLocalePrefs: UpdateUserLocaleRequest = {
    defaultLocale: DefaultDisplayLocale,
    defaultVoiceLocale: DefaultVoiceLocale,
};

export const useCurrentUserStore = create<CurrentUserState>()((set, get) => {
    const getValidatedUserLocales = (request: UpdateUserLocaleRequest): UpdateUserLocaleRequest => {
        const defaultLocale = getSupportedLocale(request.defaultLocale, 'display') ?? DefaultDisplayLocale;
        const defaultVoiceLocale = getSupportedLocale(request.defaultVoiceLocale, 'voice') ?? DefaultVoiceLocale;
        return { defaultLocale, defaultVoiceLocale };
    };

    const changeAppLanguage = (displayLocale: string) => {
        if (i18n.language !== displayLocale) {
            i18n.changeLanguage(displayLocale, (error) => {
                if (error) {
                    console.error(`Unsupported language ${displayLocale}. Defaulting to ${DefaultDisplayLocale}`);
                } else {
                    localStorage.setItem('displayLocale', displayLocale);
                }
            });
        }
    };

    const informUserOfFallbackModel = (modelUrl: string, voiceLocale: string) => {
        if (voiceLocale !== DefaultVoiceLocale && modelUrl === fallbackVoiceUrl) {
            set({ showUserFallbackDialog: true });
        }
    };

    const updateVoiceModelUrl = (modelUrl: string) => {
        if (modelUrl !== get().voiceModelUrl) {
            localStorage.setItem('VOICE_MODEL_URL', modelUrl);
            set({ voiceModelUrl: modelUrl });
        }
    };

    const loadVoiceModelUrl = async (
        voiceLocale: string,
        isMainWindow: boolean,
        requestUrl?: string
    ): Promise<string> => {
        let modelUrl = requestUrl ?? localStorage.getItem('VOICE_MODEL_URL') ?? fallbackVoiceUrl;
        try {
            set({ isLoading: true });
            if (isMainWindow) {
                const result = await enabledApi.getVoiceModelReferenceForLocale(voiceLocale, 'tar.gz');
                if (result.resultType === 'Ok' && result.data) {
                    modelUrl = result.data.fileUrl;
                }
            }
        } catch (error) {
            set({ errors: [`Error loading voice model. ${error}`] });
        } finally {
            updateVoiceModelUrl(modelUrl);
            informUserOfFallbackModel(modelUrl, voiceLocale);
            set({ isLoading: false });
        }
        return modelUrl;
    };

    const setShowUserFallbackDialog = (v: boolean) => set({ showUserFallbackDialog: v });
    const setSignalRConnected = (v: boolean) => set({ signalRConnected: v });
    const setPendingTosConfig = (config: TosConfigModel | null) => set({ pendingTosConfig: config });

    const isFreeUser = () => {
        const acc = get().userLicenseAccess;
        return !(acc?.isDeveloperAccess || acc?.isEmployeeAccess || acc?.isStudentAccess || acc?.isPlusAccess);
    };
    const isProUser = () => get().userLicenseAccess?.isEmployeeAccess;
    const isEduUser = () => get().userLicenseAccess?.isStudentAccess;
    const isSdkUser = () => get().userLicenseAccess?.isDeveloperAccess;
    const isPlusUser = () => get().userLicenseAccess?.isPlusAccess;
    const isTrialUser = () => {
        const acc = get().userLicenseAccess;
        return (
            acc?.licenseExpirationDate !== undefined &&
            acc.hasLicense === false &&
            !!acc.licenseExpirationDate &&
            new Date(acc.licenseExpirationDate) > new Date()
        );
    };

    const getUserFeatureFlags = () => {
        const { userLicenseAccess, currentUser } = get();
        const userFlags: string[] = [...PersonalLicenseEnabledFeatureFlags];
        if (userLicenseAccess) {
            for (const licenseKey of Object.keys(LicenseEnabledFeatureFlags) as OrganizationModelLicenseKeys[]) {
                if (userLicenseAccess[licenseKey as keyof OrganizationModelLicense]) {
                    userFlags.push(...LicenseEnabledFeatureFlags[licenseKey]);
                }
            }
        }
        if (currentUser?.userPreferences?.featureFlags) {
            for (const flag of currentUser.userPreferences.featureFlags) {
                if (BetaAllFeatureFlags.includes(flag)) userFlags.push(flag);
            }
        }
        return [...new Set(userFlags)];
    };

    const isUserLicensedForAllFeatureFlags = (flagNames: string[]) => {
        if (!get().userLicenseAccess) return false;
        return flagNames.every((flag) => get().isFeatureFlagEnabled(flag));
    };

    const isFeatureFlagEnabled = (flagName: string) => {
        if (PersonalLicenseEnabledFeatureFlags.includes(flagName)) return true;
        const acc = get().userLicenseAccess;
        if (acc) {
            for (const licenseKey of Object.keys(LicenseEnabledFeatureFlags) as OrganizationModelLicenseKeys[]) {
                if (
                    acc[licenseKey as keyof OrganizationModelLicense] &&
                    LicenseEnabledFeatureFlags[licenseKey].includes(flagName)
                ) {
                    return true;
                }
            }
        }
        if (!BetaAllFeatureFlags.includes(flagName)) return false;
        return get().currentUser?.userPreferences?.featureFlags?.includes(flagName) ?? false;
    };

    const initializeFromStorage = async (isMainWindow: boolean = true) => {
        try {
            console.log('INITIALIZING USER FROM STORAGE');
            const storedUser = localStorage.getItem(StorageIdUser);
            if (storedUser) {
                const currentUserModel: UserWithCapabilitiesModel = JSON.parse(storedUser);
                set({
                    currentUser: currentUserModel,
                });
                await get().updateUserLocales(
                    {
                        defaultLocale: currentUserModel.defaultLocale,
                        defaultVoiceLocale: currentUserModel.defaultVoiceLocale,
                    },
                    isMainWindow
                );
                const userPermission = currentUserModel.isApprovedDomain ?? false;
                set({
                    isApprovedDomain: userPermission,
                    isEmailVerified: currentUserModel.isEmailVerified ?? false,
                });
                publish('userUpdated', { user: currentUserModel });
            }
            get().loadUserLicenseAccessFromStorage();
        } catch (e) {
            console.log(e);
        }
    };

    const deleteCurrentUser = async () => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.softDeleteCurrentUser();
            if (result?.resultType === 'Ok') {
                localStorage.removeItem(StorageIdUser);
                set({ currentUser: null });
            }
            set({ isLoading: false });
            return result;
        } catch (e) {
            console.log(e);
            set({ isLoading: false });
            return new UnexpectedResult<UserModel>();
        }
    };

    const loadCurrentUser = async () => {
        try {
            set({ isLoading: true });
            const userAuthPermissions = await enabledApi.validateUserAuthClientPermission();
            let userPermission = false;
            if (
                userAuthPermissions.resultType !== 'Ok' &&
                userAuthPermissions?.errors != null &&
                userAuthPermissions.errors.length > 0
            ) {
                set({ errors: [...userAuthPermissions.errors] });
            } else if (userAuthPermissions?.resultType === 'Ok') {
                userPermission = true;
            }
            set({ isApprovedDomain: userPermission });

            const result = await enabledApi.getUserWithCapabilitiess();
            if (result?.resultType === 'Ok' && result.data) {
                get().handleUpdateUser(result.data);
                await get().updateUserLocales({
                    defaultLocale: result.data.defaultLocale,
                    defaultVoiceLocale: result.data.defaultVoiceLocale,
                });
            } else if (result?.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
            set({ isLoading: false });
            return result;
        } catch (e) {
            set({ isLoading: false });
            return new UnexpectedResult<UserWithCapabilitiesModel>();
        }
    };

    const updatePassword = async (oldPassword: string, newPassword: string, confirmNewPassword: string) => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.changePassword({
                oldPassword,
                newPassword,
                newPasswordConfirmation: confirmNewPassword,
            });
            set({ isLoading: false });
            return result;
        } catch (e) {
            set({ isLoading: false });
            return new UnexpectedResult<UserModel>();
        }
    };

    const loadCapabilityFacetGroups = async () => {
        try {
            set({ isLoadingFacets: true });
            const result = await enabledApi.getCapabilityFacets();
            if (result?.resultType === 'Ok' && result.data) {
                set({ capabilityFacetGroups: result.data });
            }
            set({ isLoadingFacets: false });
            return result;
        } catch (e) {
            set({ isLoadingFacets: false });
            return new UnexpectedResult<CapabilityFacetGroupModel[]>();
        }
    };

    const updateUserCapabilities = async (request: UpdateUserCapabilitiesRequest) => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.updateUserCapabilities(request);
            if (result?.resultType === 'Ok' && result.data) get().handleUpdateUser(result.data);
            set({ isLoading: false });
            return result;
        } catch (e) {
            set({ isLoading: false });
            return new UnexpectedResult<UserWithCapabilitiesModel>();
        }
    };

    const updateEmailPreferences = async (request: UpdateEmailPreferencesRequest) => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.updateUserEmailPreferences(request);
            if (result?.resultType === 'Ok') {
                const updatedUser = { ...get().currentUser, ...result.data } as UserWithCapabilitiesModel;
                get().handleUpdateUser(updatedUser);
            }
            set({ isLoading: false });
            return result;
        } catch (e) {
            set({ isLoading: false });
            return new UnexpectedResult<UserModel>();
        }
    };

    const updateUserPreferences = async (request: UserPreferencesModel) => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.updateUserPreferences(request);
            if (result?.resultType === 'Ok') {
                const updatedUser = { ...get().currentUser, ...result.data } as UserWithCapabilitiesModel;
                get().handleUpdateUser(updatedUser);
            } else if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
            set({ isLoading: false });
            return result;
        } catch (error) {
            set({ errors: [String(error)], isLoading: false });
            return new UnexpectedResult<UserModel>();
        }
    };

    const getUserPreferences = async () => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.getUserPreferences();
            if (result?.resultType === 'Ok') {
                set({ isLoading: false });
                return result;
            } else if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
        } catch (error) {
            set({ errors: [String(error)], isLoading: false });
            return new UnexpectedResult<UserPreferencesModel>();
        }
    };

    const requestEmailVerificationResend = async () => {
        try {
            const result = await enabledApi.requestEmailVerification();
            if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
            return result;
        } catch (error) {
            set({ errors: [String(error)] });
            return new UnexpectedResult<UserModel>();
        }
    };

    const clearCurrentUser = () => {
        set({ currentUser: null });
    };

    const updateUserLocales = async (request: UpdateUserLocaleRequest, isMainWindow: boolean = true) => {
        const localePreferences = getValidatedUserLocales(request);
        changeAppLanguage(localePreferences.defaultLocale);

        const modelUrl = await loadVoiceModelUrl(
            localePreferences.defaultVoiceLocale,
            isMainWindow,
            localePreferences.voiceModelUrl
        );

        const current = get().userLocalePreferences;
        const hasChanges =
            localePreferences.defaultLocale !== current.defaultLocale ||
            localePreferences.defaultVoiceLocale !== current.defaultVoiceLocale;

        if (hasChanges) {
            const updated = { ...localePreferences, voiceModelUrl: modelUrl };
            set({ userLocalePreferences: updated });
            publish('userLocalePreferencesUpdated', { localePreferences });
        }
    };

    const saveUserLocales = async (request: UpdateUserLocaleRequest) => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.updateUserLocales(request);
            if (result?.resultType === 'Ok' && result.data) {
                await get().updateUserLocales(request);
                const updatedUser = { ...get().currentUser, ...result.data };
                get().handleUpdateUser(updatedUser);
            } else if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
            set({ isLoading: false });
            return result;
        } catch (error) {
            set({ errors: [String(error)], isLoading: false });
            return new UnexpectedResult<UserModel>();
        }
    };

    const handleUpdateUser = (user: UserWithCapabilitiesModel) => {
        const updatedUser = { ...user, isApprovedDomain: get().isApprovedDomain };
        set({ currentUser: updatedUser });
        localStorage.setItem(StorageIdUser, JSON.stringify(updatedUser));
        if (updatedUser.isEmailVerified !== get().isEmailVerified) {
            set({ isEmailVerified: updatedUser.isEmailVerified });
        }
        publish('userUpdated', { user: updatedUser });
    };

    const updateUserLicenseAccess = async (access: UserLicenseAccessModel, updateLocal: boolean) => {
        const currentAccess = get().userLicenseAccess;
        const hasChanged = !currentAccess || JSON.stringify(currentAccess) !== JSON.stringify(access);
        if (!hasChanged) return;

        const licenseType = computeUserLicenseType(access);
        set({
            userLicenseAccess: access,
            userLicenseType: licenseType,
            userBetaFeatureFlags: computeUserBetaFeatureFlags(access),
            hasNoOrgMemberships: computeHasNoOrgMemberships(access),
            isLicenseExpired: computeIsLicenseExpired(access),
        });
        if (updateLocal) {
            localStorage.setItem(StorageIdUserAccess, JSON.stringify({ access, type: licenseType }));
        }
        publish('userLicenseChangeLocal', { access, type: licenseType });
    };

    const loadUserLicenseAccess = async () => {
        try {
            const result = await enabledApi.getUserLicenseAccess();
            if (result?.resultType === 'Ok' && result.data) {
                await get().updateUserLicenseAccess(result.data, true);
            } else if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
                get().loadUserLicenseAccessFromStorage();
            }
        } catch (error) {
            set({ errors: [error] });
            get().loadUserLicenseAccessFromStorage();
        }
    };

    const loadUserLicenseAccessFromStorage = () => {
        const userAccess = localStorage.getItem(StorageIdUserAccess);
        if (userAccess) {
            const { access, type } = JSON.parse(userAccess) as IUserAccess;
            get().updateUserLicenseAccess(access, false);
            return { access, type };
        }
    };

    const updateUserPermissions = (value: boolean) => {
        set({ isApprovedDomain: value });
        const currUser = get().currentUser;
        if (currUser) get().handleUpdateUser(currUser);
    };

    const checkAndUpdatePendingTos = async () => {
        try {
            const tosResult = await enabledApi.getTosConfig();
            if (tosResult?.resultType === 'Ok' && tosResult.data) {
                const acceptedVersion = get().currentUser?.acceptedTosVersion;
                const requiredVersion = tosResult.data.requiredVersion;
                if (!acceptedVersion || acceptedVersion < requiredVersion) {
                    set({ pendingTosConfig: tosResult.data });
                }
            }
        } catch (error) {
            console.error('Error checking TOS config:', error);
        }
    };

    const addUpgradeBanner = (newBanner: IBannerModel, duration?: number) => {
        const currentMap = get().upgradeBannerMap;
        const updatedMap = new Map(currentMap);
        // clear existing timeout if banner already present
        const existing = updatedMap.get(newBanner.id);
        if (existing?.timeout) clearTimeout(existing.timeout);

        let timeout: NodeJS.Timeout | undefined;
        if (duration !== undefined) {
            timeout = setTimeout(() => {
                get().removeUpgradeBanner(newBanner.id);
            }, duration);
        }
        updatedMap.set(newBanner.id, { ...newBanner, timeout });
        set({ upgradeBannerMap: updatedMap, upgradeBannersArray: Array.from(updatedMap.values()) });
    };

    const removeUpgradeBanner = (id: string) => {
        const currentMap = get().upgradeBannerMap;
        const updatedMap = new Map(currentMap);
        const banner = updatedMap.get(id);
        if (banner?.timeout) clearTimeout(banner.timeout);
        updatedMap.delete(id);
        set({ upgradeBannerMap: updatedMap, upgradeBannersArray: Array.from(updatedMap.values()) });
    };

    return {
        // State
        capabilityFacetGroups: [],
        currentUser: null,
        errors: [],
        hasNoOrgMemberships: true,
        isApprovedDomain: false,
        isEmailVerified: false,
        isLicenseExpired: false,
        isLoading: false,
        isLoadingFacets: false,
        pendingTosConfig: null,
        showUserFallbackDialog: false,
        signalRConnected: true,
        upgradeBannerMap: new Map<string, IBannerModel>(),
        upgradeBannersArray: [],
        userBetaFeatureFlags: [...BetaLicenseFeatureFlags['personal']],
        userLicenseAccess: null,
        userLicenseType: 'personal',
        userLocalePreferences: defaultLocalePrefs,
        voiceModelUrl: fallbackVoiceUrl,

        // Actions
        addUpgradeBanner,
        checkAndUpdatePendingTos,
        clearCurrentUser,
        deleteCurrentUser,
        getUserFeatureFlags,
        getUserPreferences,
        handleUpdateUser,
        initializeFromStorage,
        isEduUser,
        isFeatureFlagEnabled,
        isFreeUser,
        isPlusUser,
        isProUser,
        isSdkUser,
        isTrialUser,
        isUserLicensedForAllFeatureFlags,
        loadCapabilityFacetGroups,
        loadCurrentUser,
        loadUserLicenseAccess,
        loadUserLicenseAccessFromStorage,
        removeUpgradeBanner,
        requestEmailVerificationResend,
        saveUserLocales,
        setPendingTosConfig,
        setShowUserFallbackDialog,
        setSignalRConnected,
        updateEmailPreferences,
        updatePassword,
        updateUserCapabilities,
        updateUserLicenseAccess,
        updateUserLocales,
        updateUserPermissions,
        updateUserPreferences,
    };
});

// Module-level subscribe: re-publish feature flags whenever user or license changes
useCurrentUserStore.subscribe((state, prevState) => {
    if (state.currentUser !== prevState.currentUser || state.userLicenseAccess !== prevState.userLicenseAccess) {
        const featureFlags = useCurrentUserStore.getState().getUserFeatureFlags();
        publish('userFeatureFlagsUpdated', { featureFlags });
    }
});

// Replace the buggy no-dep-array useEffect with a single module-level subscription
subscribe('updateUserLicenseAccess', async () => {
    await useCurrentUserStore.getState().loadUserLicenseAccess();
});
